import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";
import { translateStatus, translateRisk } from "../../lib/constants";

// Entity Detail Page Component
export const EntityDetailPage = ({ type }) => {
  const { id } = useParams();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [validatingVies, setValidatingVies] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPortalAccessModal, setShowPortalAccessModal] = useState(false);
  const [portalAccess, setPortalAccess] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [tenantName, setTenantName] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const canManage = user?.role === 'super_admin' || user?.role === 'tenant_admin' || user?.role === 'hr_manager';
  const isSuperAdmin = user?.role === 'super_admin';

  const fetchEntity = async () => {
    try {
      const endpoint = type === 'client' ? `/clients/${id}` : type === 'supplier' ? `/suppliers/${id}` : `/employees/${id}`;
      const response = await api.get(endpoint);
      setEntity(response.data);
      
      // Fetch tenant name for super admin
      if (isSuperAdmin && response.data.tenant_id) {
        try {
          const tenantRes = await api.get(`/tenants/${response.data.tenant_id}`);
          setTenantName(tenantRes.data?.name);
        } catch (e) {
          console.error('Error fetching tenant:', e);
        }
      }
      
      // Check portal access for employees
      if (type === 'employee') {
        try {
          const portalRes = await api.get(`/users/by-employee/${id}`);
          setPortalAccess(portalRes.data);
        } catch (e) {
          console.error('Error checking portal access:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching entity:', error);
      toast.error('Erro ao carregar dados');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntity();
  }, [id, type]);

  const handleEnrichData = async () => {
    setEnriching(true);
    try {
      const endpoint = type === 'client' ? `/clients/${id}/enrich` : `/suppliers/${id}/enrich`;
      const response = await api.post(endpoint);
      toast.success(`Dados enriquecidos com sucesso! ${response.data.updated_fields?.length || 0} campos atualizados.`);
      fetchEntity();
    } catch (error) {
      console.error('Error enriching data:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao enriquecer dados';
      toast.error(errorMsg);
    } finally {
      setEnriching(false);
    }
  };

  const handleValidateVies = async () => {
    setValidatingVies(true);
    try {
      const response = await api.post(`/clients/${id}/validate-vies`);
      if (response.data.valid) {
        toast.success('VAT/NIF validado com sucesso no VIES!');
      } else {
        toast.warning('VAT/NIF não encontrado no VIES');
      }
      fetchEntity();
    } catch (error) {
      console.error('Error validating VIES:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao validar VIES';
      toast.error(errorMsg);
    } finally {
      setValidatingVies(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      const endpoint = type === 'client' ? `/clients/${id}` : type === 'supplier' ? `/suppliers/${id}` : `/employees/${id}`;
      await api.delete(endpoint);
      toast.success(`${type === 'client' ? 'Cliente' : type === 'supplier' ? 'Fornecedor' : 'Colaborador'} eliminado com sucesso`);
      navigate(type === 'client' ? '/clients' : type === 'supplier' ? '/suppliers' : '/employees');
    } catch (error) {
      console.error('Error deleting entity:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao eliminar registo';
      toast.error(errorMsg);
    } finally {
      setActionLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleInactivate = async () => {
    setActionLoading(true);
    try {
      const endpoint = type === 'client' ? `/clients/${id}/inactivate` : `/suppliers/${id}/inactivate`;
      await api.put(endpoint);
      toast.success(`${type === 'client' ? 'Cliente' : 'Fornecedor'} inativado com sucesso`);
      fetchEntity();
    } catch (error) {
      console.error('Error inactivating entity:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao inativar registo';
      toast.error(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      const endpoint = type === 'client' ? `/clients/${id}` : `/suppliers/${id}`;
      await api.put(endpoint, { is_active: true, status: 'pending' });
      toast.success(`${type === 'client' ? 'Cliente' : 'Fornecedor'} reativado com sucesso`);
      fetchEntity();
    } catch (error) {
      console.error('Error reactivating entity:', error);
      toast.error('Erro ao reativar registo');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!entity) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-muted rounded-lg" data-testid="back-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">{entity.name}</h1>
              {isSuperAdmin && tenantName && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  {tenantName}
                </span>
              )}
            </div>
            <p className="text-muted-foreground">{type === 'client' ? 'Cliente' : type === 'supplier' ? 'Fornecedor' : 'Colaborador'}</p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowEditModal(true)}
            className="px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors flex items-center gap-2"
            data-testid="edit-entity-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Editar
          </button>
          
          {type === 'employee' && canManage && (
            <button
              onClick={() => setShowStatusModal(true)}
              className="px-4 py-2 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition-colors flex items-center gap-2"
              data-testid="change-status-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Alterar Status
            </button>
          )}
          
          {type === 'employee' && canManage && (
            portalAccess?.has_access ? (
              <div className="px-4 py-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 flex items-center gap-2" data-testid="portal-access-badge">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-sm font-medium">Acesso Portal Ativo</span>
                <span className="text-xs">({portalAccess.user?.email})</span>
              </div>
            ) : (
              <button
                onClick={() => setShowPortalAccessModal(true)}
                className="px-4 py-2 rounded-lg bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors flex items-center gap-2"
                data-testid="create-portal-access-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Criar Acesso Portal
              </button>
            )
          )}
          
          {(type === 'client' || type === 'supplier') && (
            <>
              <button
                onClick={handleEnrichData}
                disabled={enriching}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium hover:from-violet-600 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid="enrich-data-btn"
              >
                {enriching ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    A enriquecer...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Enriquecer com IA
                  </>
                )}
              </button>
              
              {canManage && (
                entity.is_active !== false ? (
                  <button
                    onClick={handleInactivate}
                    disabled={actionLoading}
                    className="px-4 py-2 rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                    data-testid="inactivate-entity-btn"
                  >
                    {actionLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    )}
                    Inativar
                  </button>
                ) : (
                  <button
                    onClick={handleReactivate}
                    disabled={actionLoading}
                    className="px-4 py-2 rounded-lg bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                    data-testid="reactivate-entity-btn"
                  >
                    {actionLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    Reativar
                  </button>
                )
              )}
              
              {type === 'client' && (
                <button
                  onClick={handleValidateVies}
                  disabled={validatingVies || !entity.tax_id}
                  className="px-4 py-2 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="validate-vies-btn"
                >
                  {validatingVies ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      A validar...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Validar VIES
                    </>
                  )}
                </button>
              )}
            </>
          )}
          
          {canManage && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-colors flex items-center gap-2"
              data-testid="delete-entity-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Eliminar
            </button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-card rounded-xl border border-border w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold">Confirmar Eliminação</h3>
            </div>
            <p className="text-muted-foreground mb-6">
              Tem a certeza que deseja eliminar <strong>{entity.name}</strong>? 
              Esta ação irá também eliminar todos os documentos e conflitos associados. 
              Esta ação não pode ser revertida.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted"
                data-testid="cancel-delete-btn"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="px-4 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50"
                data-testid="confirm-delete-btn"
              >
                {actionLoading ? 'A eliminar...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Employee Status Modal */}
      {showStatusModal && type === 'employee' && (
        <EmployeeStatusModal
          employee={entity}
          onClose={() => setShowStatusModal(false)}
          onSave={() => { setShowStatusModal(false); fetchEntity(); }}
        />
      )}

      {/* Portal Access Modal */}
      {showPortalAccessModal && type === 'employee' && (
        <PortalAccessModal
          employee={entity}
          onClose={() => setShowPortalAccessModal(false)}
          onSave={() => { setShowPortalAccessModal(false); fetchEntity(); }}
        />
      )}

      {/* Edit Entity Modal */}
      {showEditModal && (
        <EntityEditModal 
          entity={entity} 
          type={type} 
          onClose={() => setShowEditModal(false)} 
          onSave={() => { setShowEditModal(false); fetchEntity(); }} 
        />
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">Informações Gerais</h2>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  entity.status === 'approved' || entity.status === 'active' ? 'status-badge-approved' :
                  entity.status === 'rejected' || entity.status === 'terminated' ? 'status-badge-rejected' : 'status-badge-pending'
                }`}>
                  {translateStatus(entity.status)}
                </span>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Nível de Risco</dt>
              <dd>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  entity.risk_level === 'high' ? 'risk-badge-high' :
                  entity.risk_level === 'medium' ? 'risk-badge-medium' : 'risk-badge-low'
                }`}>
                  {translateRisk(entity.risk_level)}
                </span>
              </dd>
            </div>
            {entity.tax_id && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">NIF</dt>
                <dd className="flex items-center gap-2">
                  {entity.tax_id}
                  {entity.vies_verified && (
                    <span className="px-1.5 py-0.5 rounded text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400" title="Validado VIES">
                      VIES ✓
                    </span>
                  )}
                </dd>
              </div>
            )}
            {entity.email && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Email</dt>
                <dd>{entity.email}</dd>
              </div>
            )}
            {entity.department && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Departamento</dt>
                <dd>{entity.department}</dd>
              </div>
            )}
            {entity.position && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Cargo</dt>
                <dd>{entity.position}</dd>
              </div>
            )}
            {entity.country && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">País</dt>
                <dd>{entity.country}</dd>
              </div>
            )}
            {entity.city && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Cidade</dt>
                <dd>{entity.city}</dd>
              </div>
            )}
            {entity.industry && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Indústria</dt>
                <dd>{entity.industry}</dd>
              </div>
            )}
            {entity.website && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Website</dt>
                <dd><a href={entity.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{entity.website}</a></dd>
              </div>
            )}
          </dl>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Score de Risco</h2>
            <div className="flex items-center gap-4">
              <div className="text-5xl font-bold font-['Plus_Jakarta_Sans']">
                {Math.round(entity.risk_score * 100)}
              </div>
              <div className="flex-1">
                <div className="h-4 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      entity.risk_level === 'high' ? 'bg-red-500' :
                      entity.risk_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${entity.risk_score * 100}%` }}
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-1">Score de risco calculado por IA</p>
              </div>
            </div>
          </div>

          {entity.enrichment_data && (
            <div className="rounded-xl border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Dados Enriquecidos</h2>
                {entity.enrichment_date && (
                  <span className="text-xs text-muted-foreground">
                    Atualizado: {new Date(entity.enrichment_date).toLocaleDateString('pt-PT')}
                  </span>
                )}
              </div>
              <div className="space-y-3 text-sm">
                {entity.enrichment_data.public_info_summary && (
                  <p className="text-muted-foreground">{entity.enrichment_data.public_info_summary}</p>
                )}
                {entity.enrichment_data.risk_indicators && entity.enrichment_data.risk_indicators.length > 0 && (
                  <div>
                    <p className="font-medium mb-1">Indicadores de Risco:</p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1">
                      {entity.enrichment_data.risk_indicators.map((indicator, idx) => (
                        <li key={idx}>{indicator}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Financial Information Tab - For Clients and Suppliers */}
      {(type === 'client' || type === 'supplier') && (
        <FinancialInfoSection entity={entity} type={type} entityId={id} onUpdate={() => fetchEntity()} />
      )}

      {/* Entity Documents Section */}
      <EntityDocumentsSection entityType={type} entityId={id} tenantId={entity.tenant_id} />

      {/* Beneficial Owners / Shareholders / Family Members */}
      <RelatedPersonsSection entity={entity} type={type} entityId={id} onUpdate={() => fetchEntity()} />

      {/* External Positions for Employees */}
      {type === 'employee' && (
        <ExternalPositionsSection entity={entity} entityId={id} onUpdate={() => fetchEntity()} />
      )}

      {/* Equipment and Vehicles for Employees */}
      {type === 'employee' && (
        <EmployeeAssetsSection employeeId={id} employeeName={entity?.name} />
      )}
    </div>
  );
};

// Employee Assets Section (Equipment and Vehicles)
const EmployeeAssetsSection = ({ employeeId, employeeName }) => {
  const [equipment, setEquipment] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('equipment');

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const [eqRes, vehRes] = await Promise.all([
          api.get(`/hr/equipment/employee/${employeeId}`),
          api.get(`/hr/vehicles/employee/${employeeId}`)
        ]);
        setEquipment(eqRes.data || []);
        setVehicles(vehRes.data || []);
      } catch (error) {
        console.error('Error fetching assets:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAssets();
  }, [employeeId]);

  const getEquipmentTypeIcon = (type) => {
    const icons = {
      laptop: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      desktop: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      phone: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
      tablet: 'M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z',
      monitor: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
    };
    return icons[type] || icons.laptop;
  };

  if (loading) {
    return <div className="animate-pulse bg-muted h-32 rounded-xl"></div>;
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="employee-assets-section">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="font-semibold">Ativos Atribuídos</h3>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('equipment')}
            className={`px-3 py-1 rounded-lg text-sm ${activeTab === 'equipment' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
          >
            Equipamentos ({equipment.length})
          </button>
          <button
            onClick={() => setActiveTab('vehicles')}
            className={`px-3 py-1 rounded-lg text-sm ${activeTab === 'vehicles' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
          >
            Viaturas ({vehicles.length})
          </button>
        </div>
      </div>
      
      <div className="p-4">
        {activeTab === 'equipment' && (
          <>
            {equipment.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Nenhum equipamento atribuído</p>
            ) : (
              <div className="grid gap-3">
                {equipment.map((eq) => (
                  <div key={eq.equipment_id} className="flex items-center gap-4 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getEquipmentTypeIcon(eq.equipment_type)} />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{eq.brand} {eq.model}</p>
                      <p className="text-sm text-muted-foreground">
                        {eq.equipment_type?.toUpperCase()} • SN: {eq.serial_number || 'N/A'} • Asset: {eq.asset_tag || 'N/A'}
                      </p>
                    </div>
                    <div className="text-right text-sm">
                      {eq.warranty_expiry && (
                        <p className="text-muted-foreground">Garantia até {eq.warranty_expiry}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'vehicles' && (
          <>
            {vehicles.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Nenhuma viatura atribuída</p>
            ) : (
              <div className="grid gap-3">
                {vehicles.map((veh) => (
                  <div key={veh.vehicle_id} className="flex items-center gap-4 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{veh.license_plate} - {veh.brand} {veh.model}</p>
                      <p className="text-sm text-muted-foreground">
                        {veh.vehicle_type?.toUpperCase()} • {veh.year} • {veh.current_km?.toLocaleString()} km
                      </p>
                    </div>
                    <div className="text-right text-sm">
                      {veh.inspection_expiry && (
                        <p className="text-muted-foreground">Inspeção até {veh.inspection_expiry}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Employee Status Modal
const EmployeeStatusModal = ({ employee, onClose, onSave }) => {
  const [status, setStatus] = useState(employee.status || 'active');
  const [terminationDate, setTerminationDate] = useState('');
  const [terminationReason, setTerminationReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/employees/${employee.employee_id}/status`, {
        status,
        termination_date: terminationDate || null,
        termination_reason: terminationReason || null
      });
      toast.success('Status atualizado com sucesso');
      onSave();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold">Alterar Status do Colaborador</h3>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              data-testid="status-select"
            >
              <option value="active">Ativo</option>
              <option value="on_leave">De Licença</option>
              <option value="suspended">Suspenso</option>
              <option value="terminated">Cessado</option>
            </select>
          </div>
          {status === 'terminated' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Data de Cessação</label>
                <input
                  type="date"
                  value={terminationDate}
                  onChange={(e) => setTerminationDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  data-testid="termination-date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Motivo</label>
                <textarea
                  value={terminationReason}
                  onChange={(e) => setTerminationReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background resize-none"
                  placeholder="Motivo da cessação..."
                  data-testid="termination-reason-input"
                />
              </div>
            </>
          )}
          <div className="flex gap-3 pt-4">
            <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50"
              data-testid="save-status-btn"
            >
              {saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Portal Access Modal
const PortalAccessModal = ({ employee, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    email: employee.email || '',
    password: '',
    confirmPassword: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (formData.password !== formData.confirmPassword) {
      toast.error('As passwords não coincidem');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('A password deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (!formData.email) {
      toast.error('Email é obrigatório');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/users/create-portal-access', {
        employee_id: employee.employee_id,
        email: formData.email,
        password: formData.password
      });
      toast.success('Acesso ao portal criado com sucesso');
      onSave();
    } catch (error) {
      console.error('Error creating portal access:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar acesso ao portal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold">Criar Acesso ao Portal Self-Service</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Criar credenciais de acesso para <strong>{employee.name}</strong>
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700">
            <p className="text-sm text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              O colaborador terá acesso apenas ao Portal Self-Service.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              placeholder="email@empresa.pt"
              data-testid="portal-email-input"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">Email para login no portal</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Password *</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              placeholder="Mínimo 6 caracteres"
              data-testid="portal-password-input"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Confirmar Password *</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              placeholder="Repetir password"
              data-testid="portal-confirm-password-input"
              required
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !formData.email || !formData.password || !formData.confirmPassword}
              className="flex-1 px-4 py-2 rounded-lg bg-emerald-500 text-white font-medium hover:bg-emerald-600 disabled:opacity-50"
              data-testid="create-portal-access-submit"
            >
              {saving ? 'A criar...' : 'Criar Acesso'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Entity Edit Modal
const EntityEditModal = ({ entity, type, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: entity.name || '',
    email: entity.email || '',
    tax_id: entity.tax_id || '',
    country: entity.country || '',
    city: entity.city || '',
    industry: entity.industry || '',
    website: entity.website || '',
    department: entity.department || '',
    position: entity.position || '',
    notes: entity.notes || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = type === 'client' ? `/clients/${entity.client_id}` : 
                       type === 'supplier' ? `/suppliers/${entity.supplier_id}` : 
                       `/employees/${entity.employee_id}`;
      await api.put(endpoint, formData);
      toast.success('Dados atualizados com sucesso');
      onSave();
    } catch (error) {
      console.error('Error updating entity:', error);
      toast.error('Erro ao atualizar dados');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold">Editar {type === 'client' ? 'Cliente' : type === 'supplier' ? 'Fornecedor' : 'Colaborador'}</h3>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nome *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              data-testid="edit-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              data-testid="edit-email-input"
            />
          </div>
          {type !== 'employee' && (
            <div>
              <label className="block text-sm font-medium mb-1">NIF</label>
              <input
                type="text"
                value={formData.tax_id}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                data-testid="edit-tax-id-input"
              />
            </div>
          )}
          {type === 'employee' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Departamento</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Cargo</label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                />
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">País</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cidade</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notas</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background resize-none"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !formData.name}
              className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50"
              data-testid="save-edit-btn"
            >
              {saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Financial Information Section
const FinancialInfoSection = ({ entity, type, entityId, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    annual_revenue: entity.annual_revenue || '',
    has_factoring: entity.has_factoring || false,
    has_confirming: entity.has_confirming || false
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = type === 'client' ? `/clients/${entityId}` : `/suppliers/${entityId}`;
      await api.put(endpoint, {
        annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null,
        has_factoring: type === 'client' ? formData.has_factoring : undefined,
        has_confirming: type === 'supplier' ? formData.has_confirming : undefined
      });
      toast.success('Informação financeira atualizada');
      setEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating financial info:', error);
      toast.error('Erro ao atualizar informação financeira');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Informação Financeira
        </h2>
        <button
          onClick={() => setEditing(!editing)}
          className="px-3 py-1.5 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
          data-testid="edit-financial-btn"
        >
          {editing ? 'Cancelar' : 'Editar'}
        </button>
      </div>

      {editing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Volume de Negócios Anual (€)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.annual_revenue}
              onChange={(e) => setFormData({ ...formData, annual_revenue: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              placeholder="Ex: 1000000"
              data-testid="annual-revenue-input"
            />
          </div>
          {type === 'client' && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="has_factoring"
                checked={formData.has_factoring}
                onChange={(e) => setFormData({ ...formData, has_factoring: e.target.checked })}
                className="rounded border-input"
              />
              <label htmlFor="has_factoring" className="text-sm font-medium">Factoring Ativo</label>
            </div>
          )}
          {type === 'supplier' && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="has_confirming"
                checked={formData.has_confirming}
                onChange={(e) => setFormData({ ...formData, has_confirming: e.target.checked })}
                className="rounded border-input"
              />
              <label htmlFor="has_confirming" className="text-sm font-medium">Confirming Ativo</label>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-lg border border-border hover:bg-muted">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50"
              data-testid="save-financial-btn"
            >
              {saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Dados Financeiros</h3>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt className="text-sm text-muted-foreground">Volume de Negócios</dt>
                <dd className="font-medium">{entity.annual_revenue ? `${entity.annual_revenue.toLocaleString('pt-PT')} €` : 'N/A'}</dd>
              </div>
              {type === 'client' && (
                <div className="flex justify-between">
                  <dt className="text-sm text-muted-foreground">Factoring</dt>
                  <dd>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${entity.has_factoring ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700' : 'bg-gray-100 dark:bg-gray-800 text-gray-600'}`}>
                      {entity.has_factoring ? 'Ativo' : 'Inativo'}
                    </span>
                  </dd>
                </div>
              )}
              {type === 'supplier' && (
                <div className="flex justify-between">
                  <dt className="text-sm text-muted-foreground">Confirming</dt>
                  <dd>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${entity.has_confirming ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700' : 'bg-gray-100 dark:bg-gray-800 text-gray-600'}`}>
                      {entity.has_confirming ? 'Ativo' : 'Inativo'}
                    </span>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
};

// Entity Documents Section
const EntityDocumentsSection = ({ entityType, entityId, tenantId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, [entityId, entityType]);

  const fetchDocuments = async () => {
    try {
      const response = await api.get(`/documents?entity_type=${entityType}&entity_id=${entityId}`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.document_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Erro ao descarregar documento');
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      contract: 'Contrato',
      certificate: 'Certificado',
      declaration: 'Declaração',
      id_document: 'Documento ID',
      other: 'Outro'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-center h-20">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Documentos ({documents.length})
        </h2>
      </div>

      {documents.length === 0 ? (
        <p className="text-muted-foreground text-center py-4">Nenhum documento associado</p>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div key={doc.document_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-sm">{doc.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {getCategoryLabel(doc.category)} • {(doc.file_size / 1024).toFixed(1)} KB
                    {doc.valid_until && ` • Válido até ${new Date(doc.valid_until).toLocaleDateString('pt-PT')}`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDownload(doc)}
                className="p-2 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg"
                title="Descarregar"
                data-testid={`download-doc-${doc.document_id}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Related Persons Section
const RelatedPersonsSection = ({ entity, type, entityId, onUpdate }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newPerson, setNewPerson] = useState({ name: '', tax_id: '', rcbe_code: '', role: '', percentage: '', relationship: '' });
  const [editPerson, setEditPerson] = useState({ name: '', tax_id: '', rcbe_code: '', role: '', percentage: '', relationship: '' });
  const [saving, setSaving] = useState(false);

  const getFieldName = () => {
    if (type === 'client') return 'beneficial_owners';
    if (type === 'supplier') return 'shareholders';
    return 'family_members';
  };

  const getTitle = () => {
    if (type === 'client') return 'Beneficiários Efetivos';
    if (type === 'supplier') return 'Acionistas';
    return 'Membros da Família';
  };

  const persons = entity[getFieldName()] || [];

  const handleAdd = async () => {
    setSaving(true);
    try {
      const fieldName = getFieldName();
      const updatedPersons = [...persons, newPerson];
      const endpoint = type === 'client' ? `/clients/${entityId}` : type === 'supplier' ? `/suppliers/${entityId}` : `/employees/${entityId}`;
      
      await api.put(endpoint, { [fieldName]: updatedPersons });
      toast.success('Pessoa adicionada com sucesso');
      setShowAddModal(false);
      setNewPerson({ name: '', tax_id: '', rcbe_code: '', role: '', percentage: '', relationship: '' });
      onUpdate();
    } catch (error) {
      console.error('Error adding person:', error);
      toast.error('Erro ao adicionar pessoa');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditPerson({ ...persons[index] });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const fieldName = getFieldName();
      const updatedPersons = [...persons];
      updatedPersons[editingIndex] = editPerson;
      const endpoint = type === 'client' ? `/clients/${entityId}` : type === 'supplier' ? `/suppliers/${entityId}` : `/employees/${entityId}`;
      
      await api.put(endpoint, { [fieldName]: updatedPersons });
      toast.success('Pessoa atualizada com sucesso');
      setShowEditModal(false);
      setEditingIndex(null);
      onUpdate();
    } catch (error) {
      console.error('Error updating person:', error);
      toast.error('Erro ao atualizar pessoa');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (index) => {
    if (!window.confirm('Tem a certeza que deseja remover este registo?')) return;
    try {
      const fieldName = getFieldName();
      const updatedPersons = persons.filter((_, i) => i !== index);
      const endpoint = type === 'client' ? `/clients/${entityId}` : type === 'supplier' ? `/suppliers/${entityId}` : `/employees/${entityId}`;
      
      await api.put(endpoint, { [fieldName]: updatedPersons });
      toast.success('Pessoa removida com sucesso');
      onUpdate();
    } catch (error) {
      console.error('Error removing person:', error);
      toast.error('Erro ao remover pessoa');
    }
  };

  const renderPersonForm = (personData, setPersonData, isEdit = false) => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Nome *</label>
        <input
          type="text"
          value={personData.name || ''}
          onChange={(e) => setPersonData({ ...personData, name: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
          data-testid="person-name-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">NIF</label>
        <input
          type="text"
          value={personData.tax_id || ''}
          onChange={(e) => setPersonData({ ...personData, tax_id: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
          placeholder="Número de Identificação Fiscal"
          data-testid="person-tax-id-input"
        />
      </div>
      {type === 'employee' ? (
        <div>
          <label className="block text-sm font-medium mb-1">Relação *</label>
          <select
            value={personData.relationship || ''}
            onChange={(e) => setPersonData({ ...personData, relationship: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-input bg-background"
          >
            <option value="">Selecionar</option>
            <option value="spouse">Cônjuge</option>
            <option value="parent">Pai/Mãe</option>
            <option value="child">Filho(a)</option>
            <option value="sibling">Irmão(ã)</option>
            <option value="other">Outro</option>
          </select>
        </div>
      ) : (
        <>
          <div>
            <label className="block text-sm font-medium mb-1">Cargo/Função</label>
            <input
              type="text"
              value={personData.role || ''}
              onChange={(e) => setPersonData({ ...personData, role: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              placeholder="Ex: Diretor, Sócio-Gerente"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Percentagem (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              value={personData.percentage || ''}
              onChange={(e) => setPersonData({ ...personData, percentage: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              placeholder="Ex: 25"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Código RCBE</label>
            <input
              type="text"
              value={personData.rcbe_code || ''}
              onChange={(e) => setPersonData({ ...personData, rcbe_code: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              placeholder="Código do Registo Central do Beneficiário Efetivo"
            />
          </div>
        </>
      )}
    </div>
  );

  const getRelationshipLabel = (rel) => {
    const labels = { spouse: 'Cônjuge', parent: 'Pai/Mãe', child: 'Filho(a)', sibling: 'Irmão(ã)', other: 'Outro' };
    return labels[rel] || rel;
  };

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">{getTitle()}</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-1"
          data-testid="add-person-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Adicionar
        </button>
      </div>

      {persons.length === 0 ? (
        <p className="text-muted-foreground text-center py-4">Nenhum registo</p>
      ) : (
        <div className="space-y-2">
          {persons.map((person, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
              <div className="flex-1">
                <p className="font-medium">{person.name}</p>
                <p className="text-sm text-muted-foreground">
                  {person.tax_id && <span className="mr-2">NIF: {person.tax_id}</span>}
                  {person.rcbe_code && <span className="mr-2">RCBE: {person.rcbe_code}</span>}
                  {type === 'employee' 
                    ? getRelationshipLabel(person.relationship)
                    : `${person.role || 'N/A'} ${person.percentage ? `(${person.percentage}%)` : ''}`
                  }
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleEdit(idx)}
                  className="p-1.5 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                  title="Editar"
                  data-testid={`edit-person-${idx}-btn`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleRemove(idx)}
                  className="p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                  title="Remover"
                  data-testid={`remove-person-${idx}-btn`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold">
                Adicionar {type === 'employee' ? 'Familiar' : type === 'client' ? 'Beneficiário Efetivo' : 'Acionista'}
              </h3>
            </div>
            <div className="p-6">
              {renderPersonForm(newPerson, setNewPerson)}
              <div className="flex gap-3 pt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!newPerson.name || saving}
                  className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="save-person-btn"
                >
                  {saving ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold">
                Editar {type === 'employee' ? 'Familiar' : type === 'client' ? 'Beneficiário Efetivo' : 'Acionista'}
              </h3>
            </div>
            <div className="p-6">
              {renderPersonForm(editPerson, setEditPerson, true)}
              <div className="flex gap-3 pt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={!editPerson.name || saving}
                  className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="update-person-btn"
                >
                  {saving ? 'A guardar...' : 'Atualizar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// External Positions Section for Employees
const ExternalPositionsSection = ({ entity, entityId, onUpdate }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newPosition, setNewPosition] = useState({ company: '', company_tax_id: '', rcbe_code: '', position: '', is_shareholder: false, start_date: '' });
  const [editPosition, setEditPosition] = useState({ company: '', company_tax_id: '', rcbe_code: '', position: '', is_shareholder: false, start_date: '' });
  const [saving, setSaving] = useState(false);

  const positions = entity.external_positions || [];

  const handleAdd = async () => {
    setSaving(true);
    try {
      const updatedPositions = [...positions, newPosition];
      await api.put(`/employees/${entityId}`, { external_positions: updatedPositions });
      toast.success('Posição externa adicionada');
      setShowAddModal(false);
      setNewPosition({ company: '', company_tax_id: '', rcbe_code: '', position: '', is_shareholder: false, start_date: '' });
      onUpdate();
    } catch (error) {
      console.error('Error adding position:', error);
      toast.error('Erro ao adicionar posição');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditPosition({ ...positions[index] });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const updatedPositions = [...positions];
      updatedPositions[editingIndex] = editPosition;
      await api.put(`/employees/${entityId}`, { external_positions: updatedPositions });
      toast.success('Posição externa atualizada');
      setShowEditModal(false);
      setEditingIndex(null);
      onUpdate();
    } catch (error) {
      console.error('Error updating position:', error);
      toast.error('Erro ao atualizar posição');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (index) => {
    if (!window.confirm('Tem a certeza que deseja remover esta posição externa?')) return;
    try {
      const updatedPositions = positions.filter((_, i) => i !== index);
      await api.put(`/employees/${entityId}`, { external_positions: updatedPositions });
      toast.success('Posição externa removida');
      onUpdate();
    } catch (error) {
      console.error('Error removing position:', error);
      toast.error('Erro ao remover posição');
    }
  };

  const renderPositionForm = (positionData, setPositionData) => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Empresa *</label>
        <input
          type="text"
          value={positionData.company || ''}
          onChange={(e) => setPositionData({ ...positionData, company: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
          data-testid="position-company-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">NIF da Empresa</label>
        <input
          type="text"
          value={positionData.company_tax_id || ''}
          onChange={(e) => setPositionData({ ...positionData, company_tax_id: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
          placeholder="Número de Identificação Fiscal"
          data-testid="position-tax-id-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Cargo *</label>
        <input
          type="text"
          value={positionData.position || ''}
          onChange={(e) => setPositionData({ ...positionData, position: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
          data-testid="position-role-input"
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id={`is_shareholder_${positionData === editPosition ? 'edit' : 'new'}`}
          checked={positionData.is_shareholder || false}
          onChange={(e) => setPositionData({ ...positionData, is_shareholder: e.target.checked })}
          className="rounded border-input"
        />
        <label htmlFor={`is_shareholder_${positionData === editPosition ? 'edit' : 'new'}`} className="text-sm font-medium">Sócio ou Acionista</label>
      </div>
      {positionData.is_shareholder && (
        <div>
          <label className="block text-sm font-medium mb-1">Código RCBE</label>
          <input
            type="text"
            value={positionData.rcbe_code || ''}
            onChange={(e) => setPositionData({ ...positionData, rcbe_code: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-input bg-background"
            placeholder="Código do Registo Central do Beneficiário Efetivo"
            data-testid="position-rcbe-input"
          />
        </div>
      )}
      <div>
        <label className="block text-sm font-medium mb-1">Data de Início</label>
        <input
          type="date"
          value={positionData.start_date || ''}
          onChange={(e) => setPositionData({ ...positionData, start_date: e.target.value })}
          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
          data-testid="position-start-date-input"
        />
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Posições Externas</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-1"
          data-testid="add-position-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Adicionar
        </button>
      </div>

      {positions.length === 0 ? (
        <p className="text-muted-foreground text-center py-4">Nenhuma posição externa registada</p>
      ) : (
        <div className="space-y-2">
          {positions.map((pos, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
              <div className="flex-1">
                <p className="font-medium">{pos.company} {pos.is_shareholder && <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 px-2 py-0.5 rounded ml-2">Sócio/Acionista</span>}</p>
                <p className="text-sm text-muted-foreground">
                  {pos.position} {pos.start_date && `(desde ${pos.start_date})`}
                  {pos.company_tax_id && <span className="ml-2">• NIF: {pos.company_tax_id}</span>}
                  {pos.rcbe_code && <span className="ml-2">• RCBE: {pos.rcbe_code}</span>}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleEdit(idx)}
                  className="p-1.5 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                  title="Editar"
                  data-testid={`edit-position-${idx}-btn`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleRemove(idx)}
                  className="p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                  title="Remover"
                  data-testid={`remove-position-${idx}-btn`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold">Adicionar Posição Externa</h3>
            </div>
            <div className="p-6">
              {renderPositionForm(newPosition, setNewPosition)}
              <div className="flex gap-3 pt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!newPosition.company || !newPosition.position || saving}
                  className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="save-position-btn"
                >
                  {saving ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold">Editar Posição Externa</h3>
            </div>
            <div className="p-6">
              {renderPositionForm(editPosition, setEditPosition)}
              <div className="flex gap-3 pt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={!editPosition.company || !editPosition.position || saving}
                  className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="update-position-btn"
                >
                  {saving ? 'A guardar...' : 'Atualizar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrapper components for routes
export const ClientDetailPage = () => <EntityDetailPage type="client" />;
export const SupplierDetailPage = () => <EntityDetailPage type="supplier" />;
export const EmployeeDetailPage = () => <EntityDetailPage type="employee" />;

export default EntityDetailPage;
